/**
 * SignContractDialog — typed / drawn e-signature flow.
 *
 * Used by both contractor (mode='contractor') and owner (mode='owner').
 * The same dialog adapts copy + endpoint based on mode.
 *
 * Drawn signatures use a plain HTML canvas (no extra dependency) and are
 * submitted as a PNG data URL. Typed signatures render as a styled italic
 * preview in the PDF.
 */
import React, { useEffect, useRef, useState } from 'react';
import { formatDate } from '@/lib/dateUtils';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card } from '@/components/ui/card';
import { Type, PenTool, Eraser, ShieldCheck, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

export default function SignContractDialog({
  open,
  onClose,
  contract,
  mode,                // 'contractor' | 'owner'
  contractorToken,     // required when mode='contractor'
  userId,              // required when mode='owner'
  defaultName,         // pre-fill (contractor.contact_name or owner.first_name)
  onSigned,            // (updatedContract) => void
}) {
  const [sigType, setSigType] = useState('typed');
  const [name, setName] = useState(defaultName || '');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setSigType('typed');
      setName(defaultName || '');
      setAgreed(false);
    }
  }, [open, defaultName]);

  // ---- Drawn signature canvas state ------------------------------------
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const hasDrawnRef = useRef(false);

  const beginStroke = (e) => {
    drawingRef.current = true;
    hasDrawnRef.current = true;
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const ctx = c.getContext('2d');
    ctx.strokeStyle = '#1e3a8a';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    const p = pt(e, rect);
    ctx.moveTo(p.x, p.y);
  };
  const extendStroke = (e) => {
    if (!drawingRef.current) return;
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const ctx = c.getContext('2d');
    const p = pt(e, rect);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };
  const endStroke = () => { drawingRef.current = false; };
  const pt = (e, rect) => {
    const touch = e.touches && e.touches[0];
    const cx = touch ? touch.clientX : e.clientX;
    const cy = touch ? touch.clientY : e.clientY;
    return { x: cx - rect.left, y: cy - rect.top };
  };
  const clearCanvas = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    hasDrawnRef.current = false;
  };

  // Resize canvas to its container width once mounted in 'drawn' tab
  useEffect(() => {
    if (sigType !== 'drawn' || !open) return;
    const c = canvasRef.current;
    if (!c) return;
    const parent = c.parentElement;
    c.width = Math.min(560, parent.clientWidth - 4);
    c.height = 160;
    clearCanvas();
  }, [sigType, open]);

  const submit = async () => {
    if (!name.trim()) return toast.error('Enter your printed name first');
    if (!agreed) return toast.error('Acknowledge the terms to sign');
    if (sigType === 'drawn' && !hasDrawnRef.current) {
      return toast.error('Draw your signature in the box first');
    }

    const payload = {
      signature_type: sigType,
      name: name.trim(),
      acknowledged: true,
      image_data_url: sigType === 'drawn'
        ? canvasRef.current.toDataURL('image/png')
        : null,
      contractor_token: mode === 'contractor' ? contractorToken : null,
      user_id: mode === 'owner' ? userId : null,
    };

    setSubmitting(true);
    try {
      const endpoint = mode === 'contractor'
        ? `/contracts/${contract.id}/sign-contractor`
        : `/contracts/${contract.id}/sign-owner`;
      const res = await api.post(endpoint, payload);
      toast.success(
        mode === 'contractor'
          ? 'Signed — owner has been notified to countersign.'
          : 'Countersigned — contract is fully executed.'
      );
      onSigned && onSigned(res.data);
      onClose && onClose();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Could not sign the contract';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!contract) return null;

  const title = mode === 'contractor' ? 'Sign your contract' : 'Countersign the contract';
  const subtitle = mode === 'contractor'
    ? "Your signature is recorded with timestamp + IP for compliance. Once you sign, the owner will be notified to countersign."
    : "Your countersignature finalizes the contract. The contractor will receive the fully executed PDF.";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="sm:max-w-2xl" data-testid="sign-contract-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-blue-600" />
            {title}
          </DialogTitle>
          <DialogDescription>{subtitle}</DialogDescription>
        </DialogHeader>

        {/* Contract summary */}
        <Card className="p-3 bg-slate-50 border-slate-200">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Contract</p>
              <p className="text-sm font-semibold text-slate-900 truncate">{contract.project_name}</p>
              <p className="text-[11px] text-slate-500">
                {contract.contract_amount
                  ? `$${Number(contract.contract_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : 'Amount TBD'}
                {contract.start_date && ` · starts ${formatDate(contract.start_date)}`}
              </p>
            </div>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL || ''}/api/contracts/${contract.id}/pdf?contractor_token=${encodeURIComponent(contractorToken || '')}&user_id=${encodeURIComponent(userId || '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-900 px-2.5 py-1.5 rounded-md bg-white border border-blue-200"
              data-testid="sign-dialog-preview-pdf"
            >
              <ExternalLink className="w-3 h-3" />
              Preview PDF
            </a>
          </div>
        </Card>

        {/* Signature input */}
        <Tabs value={sigType} onValueChange={setSigType}>
          <TabsList className="grid grid-cols-2 mb-3">
            <TabsTrigger value="typed" data-testid="sig-tab-typed"><Type className="w-3.5 h-3.5 mr-1.5" />Type</TabsTrigger>
            <TabsTrigger value="drawn" data-testid="sig-tab-drawn"><PenTool className="w-3.5 h-3.5 mr-1.5" />Draw</TabsTrigger>
          </TabsList>

          <TabsContent value="typed" className="space-y-2">
            <Label className="text-xs">Type your full legal name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your full name"
              data-testid="sig-typed-input"
            />
            {name && (
              <div className="mt-2 rounded-md border border-slate-200 bg-white p-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Preview</p>
                <p className="font-bold italic text-blue-800" style={{ fontSize: '28px', lineHeight: '32px' }}>
                  {name}
                </p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="drawn" className="space-y-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Printed name (recorded with the signature)</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                data-testid="sig-drawn-name"
              />
            </div>
            <div className="rounded-md border border-slate-300 bg-white touch-none">
              <canvas
                ref={canvasRef}
                onMouseDown={beginStroke}
                onMouseMove={extendStroke}
                onMouseUp={endStroke}
                onMouseLeave={endStroke}
                onTouchStart={beginStroke}
                onTouchMove={extendStroke}
                onTouchEnd={endStroke}
                className="w-full cursor-crosshair touch-none rounded-md"
                style={{ height: '160px' }}
                data-testid="sig-drawn-canvas"
              />
            </div>
            <div className="flex justify-between items-center">
              <p className="text-[11px] text-slate-500">Draw inside the box — mouse or touch</p>
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={clearCanvas}
                data-testid="sig-drawn-clear"
              >
                <Eraser className="w-3 h-3 mr-1" />
                Clear
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        {/* Acknowledgement */}
        <div className="flex items-start gap-2 pt-1">
          <Checkbox
            id="sig-ack"
            checked={agreed}
            onCheckedChange={(v) => setAgreed(!!v)}
            data-testid="sig-ack-checkbox"
            className="mt-0.5"
          />
          <label htmlFor="sig-ack" className="text-xs text-slate-700 leading-relaxed cursor-pointer select-none">
            I confirm that I have reviewed the contract and agree to its terms. By signing, I create
            a legally binding electronic signature with this IP and timestamp.
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="sign-cancel-btn">
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting} data-testid="sign-submit-btn">
            {submitting
              ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Signing…</>
              : (mode === 'contractor' ? 'Sign contract' : 'Countersign contract')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
